import {
  Modal,
  Button,
  TextInput,
  Form,
  FormGroup,
  ModalVariant,
  Select,
  SelectOption,
  SelectVariant,
  Tabs,
  TextArea,
  TabTitleText,
  Tab,
  PageSection,
} from '@patternfly/react-core'
import { isValid, parseISO } from 'date-fns'
import { useState, useCallback } from 'react'
import * as React from 'react'

import { ProgramStartOption } from '@app/domain/patient/patient.interface'
import { useReasonsForDiscontinuation } from '@app/domain/program/program.hooks'
import type {
  IProgramEnrolment} from '@app/domain/program/program.interface';
import {
  ProgramEnrolmentStatusId,
} from '@app/domain/program/program.interface'
import { formatIsoDateTimeOffset } from '@app/i18n/formats'
import { getLocalDate } from '@app/lib/time'

import type {
  SelectOptionObject} from '@patternfly/react-core';

interface Props {
  isModalOpen: boolean
  onClose: () => void
  enrolment: IProgramEnrolment
  onUpdateStartDate: (newStartDate: string) => void
  onDiscontinue: (reason: string, description: string) => void
  onRemove: () => void
  onContinue: () => void
}

enum TabKeys {
  ModifyStartDate = 'modify-start-date',
  Discontinue = 'discontinue',
  Remove = 'remove',
  ContinueEnrolment = 'continue-enrolment',
}

export const EditEnrolmentModal = ({
  isModalOpen,
  onClose,
  enrolment,
  onUpdateStartDate,
  onDiscontinue,
  onRemove,
  onContinue,
}: Props) => {
  const now = new Date()
  const defaultDate = `${getLocalDate(now)}T${now.toTimeString().slice(0, 5)}`
  const [selectedStartOption, setSelectedStartOption] = useState<
    ProgramStartOption
  >(ProgramStartOption.Custom)

  const [startDate, setStartDate] = useState<string>(() => {
    const parsedDate = new Date(formatIsoDateTimeOffset(enrolment.startDate))
    return `${getLocalDate(parsedDate)}T${parsedDate
      .toTimeString()
      .slice(0, 5)}`
  })

  const defaultTabForStatus = (status: ProgramEnrolmentStatusId): TabKeys => {
    switch (status) {
      case ProgramEnrolmentStatusId.Discontinued:
        return TabKeys.ContinueEnrolment
      case ProgramEnrolmentStatusId.Removed:
        return TabKeys.ContinueEnrolment
      default:
        return TabKeys.ModifyStartDate
    }
  }

  const [activeTabKey, setActiveTabKey] = useState<TabKeys>(
    defaultTabForStatus(enrolment.status),
  )

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const [isSelectReasonOpen, setIsSelectReasonOpen] = useState<boolean>(false)
  const [reason, setReason] = useState<string>('')
  const [reasonDescription, setReasonDescription] = useState<string>('')
  const { reasons } = useReasonsForDiscontinuation()

  const isValidDateTime = (value: string): boolean => {
    if (!value) return false
    const parsedDate = parseISO(value)
    return isValid(parsedDate)
  }

  const handleStartDateChange = useCallback((newDate: string) => {
    if (!newDate) {
      return
    }

    if (isValidDateTime(newDate)) {
      setStartDate(newDate)
    }
  }, [])

  const handleStartOptionChange = useCallback(
    (
      _event: React.MouseEvent | React.ChangeEvent,
      selection: string | SelectOptionObject,
    ) => {
      const selectedValue = selection as ProgramStartOption

      setSelectedStartOption(selectedValue)
      setIsDropdownOpen(false)

      if (selectedValue === ProgramStartOption.Now) {
        setStartDate(defaultDate)
      }
    },
    [defaultDate],
  )

  const handleConfirmDiscontinue = () => {
    onDiscontinue(reason, reasonDescription)
    onClose()
  }

  const handleConfirmRemove = () => {
    if (
      confirm(
        'Are you sure you want to remove this enrolment? Any delivered but incomplete interactions will be also be removed.',
      )
    ) {
      onRemove()
      onClose()
    }
  }

  const handleConfirmContinue = () => {
    onContinue()
    onClose()
  }

  const availableTabs = (
    status: ProgramEnrolmentStatusId,
  ): { key: TabKeys; title: string }[] => {
    const tabs: { key: TabKeys; title: string }[] = []

    if (status === ProgramEnrolmentStatusId.Enroled) {
      tabs.push({ key: TabKeys.ModifyStartDate, title: 'Modify Start Date' })
      tabs.push({ key: TabKeys.Discontinue, title: 'Discontinue' })
      tabs.push({ key: TabKeys.Remove, title: 'Remove' })
    } else {
      tabs.push({ key: TabKeys.ContinueEnrolment, title: 'Continue Enrolment' })
    }

    return tabs
  }

  return (
    <Modal
      variant={ModalVariant.medium}
      title="Edit Enrolment"
      isOpen={isModalOpen}
      onClose={onClose}
    >
      <Tabs
        activeKey={activeTabKey}
        onSelect={(_, key) => setActiveTabKey(key as TabKeys)}
      >
        {availableTabs(enrolment.status).map(({ key, title }) => (
          <Tab
            key={key}
            eventKey={key}
            title={<TabTitleText>{title}</TabTitleText>}
          />
        ))}
      </Tabs>
      {activeTabKey === TabKeys.ModifyStartDate && (
        <PageSection style={{ marginTop: '1rem' }}>
          <Form>
            <FormGroup label="Program Start Date" fieldId="start-date">
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}
              >
                <Select
                  variant={SelectVariant.single}
                  onToggle={setIsDropdownOpen}
                  onSelect={handleStartOptionChange}
                  selections={selectedStartOption}
                  isOpen={isDropdownOpen}
                  menuAppendTo="parent"
                  style={{ minWidth: '150px' }}
                >
                  <SelectOption value={ProgramStartOption.Now}>
                    {ProgramStartOption.Now}
                  </SelectOption>
                  <SelectOption value={ProgramStartOption.Custom}>
                    {ProgramStartOption.Custom}
                  </SelectOption>
                </Select>

                {selectedStartOption === ProgramStartOption.Custom && (
                  <TextInput
                    id="start-date"
                    type="datetime-local"
                    value={startDate}
                    onChange={handleStartDateChange}
                    required
                    style={{ minWidth: '200px' }}
                  />
                )}
              </div>
            </FormGroup>
            <Button key="update" onClick={() => onUpdateStartDate(startDate)}>
              Update Start Date
            </Button>
          </Form>
        </PageSection>
      )}

      {activeTabKey === TabKeys.Discontinue && (
        <PageSection style={{ marginTop: '1rem' }}>
          <Form>
            <FormGroup label="Reason" fieldId="reason" isRequired>
              <Select
                variant={SelectVariant.single}
                aria-label="Select Reason"
                onToggle={() => setIsSelectReasonOpen(!isSelectReasonOpen)}
                onSelect={(_, selection) => {
                  setReason(selection as string)
                  setReasonDescription('')
                  setIsSelectReasonOpen(false)
                }}
                selections={reason}
                isOpen={isSelectReasonOpen}
                placeholderText="Please Select"
                menuAppendTo="parent"
              >
                {reasons.map((r, index) => (
                  <SelectOption key={index} value={r} selected={reason === r} />
                ))}
              </Select>
            </FormGroup>
            <FormGroup label="Description" fieldId="description">
              <TextArea
                id="reason_description"
                value={reasonDescription}
                onChange={setReasonDescription}
              />
            </FormGroup>
            <Button
              key="discontinue"
              onClick={handleConfirmDiscontinue}
              isDisabled={!reason}
            >
              Discontinue
            </Button>
          </Form>
        </PageSection>
      )}

      {activeTabKey === TabKeys.Remove && (
        <PageSection style={{ marginTop: '1rem' }}>
          <Button key="remove" onClick={handleConfirmRemove} variant="danger">
            Remove Enrolment
          </Button>
        </PageSection>
      )}

      {activeTabKey === TabKeys.ContinueEnrolment && (
        <PageSection style={{ marginTop: '1rem' }}>
          <Button key="continue" onClick={handleConfirmContinue}>
            Continue Enrolment
          </Button>
        </PageSection>
      )}
    </Modal>
  )
}
